import {
  ChangeEvent,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import usePopup from "../../../hooks/usePopup";
import { useParams } from "react-router-dom";
import { AddDocumentProps, DocumentDropdownType } from "./types";
import { useMutation } from "react-query";
import { getDocumentTypes, saveDocument } from "../../services";
import FolderIcon from "@mui/icons-material/Folder";
import ClearIcon from "@mui/icons-material/Clear";
import { UploadDocumentContext } from ".";
import { EmrContext } from "../../index";
const AddDocument: FC<PropsWithChildren<AddDocumentProps>> = ({
  close,
  files = "image/*,application/pdf,video/*",
  submission = false,
}) => {
  const { encounterId, patientId } = useParams();
  const fileInputRef = useRef(null);
  const { showSnackbar } = usePopup();
  const { setRefetch } = useContext(UploadDocumentContext);
  const { triggerConsultationStart } = useContext(EmrContext);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentDropdownType | null>(null);
  // const [isSubmission,setIsSubmission] = useState<number | null>(null)
  const [searchText, setSearchText] = useState<string>("");
  const {
    mutate: fetchDocumentTypes,
    isLoading: documentTypesLoading,
    data: documents,
  } = useMutation(getDocumentTypes);

  const handleClearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      (fileInputRef.current as HTMLInputElement).value = ""; // Clear the input value
    }
  };

  const { mutate: postDocuments, isLoading: saveLoading } = useMutation(
    saveDocument,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 2000,
          });
          triggerConsultationStart();
          setSelectedDocument(null)
          handleClearFile();
          setRefetch(true);
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 2000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message: e?.response?.data?.responseMessage || "Something went wrong",
          open: true,
          variant: "warning",
          duration: 2000,
        });
      },
    }
  );
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    if (target?.files) {
      setSelectedFile(target.files[0]);
    }
  };

  const handleUpload = () => {
    if (selectedFile && selectedDocument) {
      const details = {
        documentId: selectedDocument?.id || null,
        newDocument: selectedDocument?.documents || "",
        encounterId: parseInt(encounterId as string),
        patientId: parseInt(patientId as string),
        status: 1,
        isSubmission: null,
      };
      postDocuments({
        details,
        file: selectedFile,
      });
    } else {
      !Boolean(selectedFile) &&
        showSnackbar({
          message: "Please select a file",
          open: true,
          variant: "warning",
          duration: 2000,
        });

      !Boolean(selectedDocument) &&
        showSnackbar({
          message: "Please select document type",
          open: true,
          variant: "warning",
          duration: 2000,
        });
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setSelectedDocument(null);
    handleClearFile();
    close && close();
  };

  const documentsType = useMemo(() => {
    if (documents?.response) {
      if (searchText != "") {
        const filtered = documents?.response.filter((i) =>
          i.documents.toLowerCase().includes(searchText.toLowerCase())
        );
        return filtered.length == 0
          ? [
              {
                id: null,
                documents: searchText,
                clinicId: 0,
                code: null,
                status: 1,
              },
            ]
          : filtered;
      } else {
        return documents?.response;
      }
    } else {
      return [];
    }
  }, [documents?.response, searchText]);

  useEffect(() => {
    submission && console.log(submission);
    fetchDocumentTypes();
  }, []);

  return (
    <div className="grid grid-cols-12 items-center justify-center gap-3">
      <div className="col-span-5">
        <Autocomplete
          options={documentsType}
          value={selectedDocument}
          loading={documentTypesLoading}
          filterOptions={(option) => option}
          onChange={(_, value) => {
            setSelectedDocument(value as DocumentDropdownType);
          }}
          renderInput={(props) => (
            <TextField
              label="Document"
              {...props}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
            />
          )}
          getOptionLabel={(option) => `${option.documents}`}
          isOptionEqualToValue={(option, value) =>
            `${option.id}` == `${value.id}`
          }
          getOptionKey={(option) => option.id || 0}
        />
      </div>
      <div className="col-span-4  flex flex-row justify-start items-center gap-3">
        <input
          id="uploadFile"
          type="file"
          className="hidden"
          ref={fileInputRef}
          accept={files}
          onChange={handleFileChange}
        />
        <Typography align="left">{selectedFile?.name}</Typography>
        <div className="flex items-center gap-3">
          <Button
            component="label"
            htmlFor="uploadFile"
            variant="text"
            endIcon={<FolderIcon />}
          >
            browse
          </Button>
          {Boolean(selectedFile) && (
            <IconButton size="small" color="error" onClick={handleClearFile}>
              <ClearIcon />
            </IconButton>
          )}
          {/* <FormControlLabel  label="Add to Claim" labelPlacement="start" control={<Checkbox size="small" onChange={(e)=>{setIsSubmission(e.target.checked ? 1 : 0)}} />} /> */}
        </div>
      </div>
      <div className="col-span-3 flex items-center justify-end gap-2">
        <Button
          color="success"
          onClick={handleUpload}
          disabled={saveLoading}
          endIcon={
            saveLoading && <CircularProgress size={18} color="success" />
          }
        >
          upload
        </Button>
        <Button color="error" onClick={handleClose}>
          cancel
        </Button>
      </div>
    </div>
  );
};

export default AddDocument;
